import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, Buildings, Palette, ArrowCounterClockwise, BellRinging, Plus, X, Briefcase } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { applyTheme, DEFAULT_THEME } from "@/lib/theme";
import CatalogSettings from "@/pages/CatalogSettings";
import HazardsPanel from "@/components/settings/HazardsPanel";
import SelfieRulesPanel from "@/components/settings/SelfieRulesPanel";
import FrontPlanDeviationPanel from "@/components/settings/FrontPlanDeviationPanel";
import PositionsCatalogPanel from "@/components/settings/PositionsCatalogPanel";
import WorksiteGeofencePanel from "@/components/settings/WorksiteGeofencePanel";
import BackupRestorePanel from "@/components/settings/BackupRestorePanel";
import DevSectionsPanel from "@/components/settings/DevSectionsPanel";
import ProjectsPanel from "@/components/settings/ProjectsPanel";
import CargosAreasPanel from "@/components/settings/CargosAreasPanel";
import SecurityPanel from "@/components/SecurityPanel";
import WeeklyReportPanel from "@/components/WeeklyReportPanel";
import { useAuth } from "@/context/AuthContext";

const PRESETS = [
  { name: "Mine Navy", primary: "#1E3A8A", accent: "#F59E0B", success: "#059669", danger: "#DC2626" },
  { name: "Copper", primary: "#7C2D12", accent: "#F97316", success: "#16A34A", danger: "#B91C1C" },
  { name: "Forest", primary: "#14532D", accent: "#EAB308", success: "#22C55E", danger: "#DC2626" },
  { name: "Slate", primary: "#0F172A", accent: "#0EA5E9", success: "#10B981", danger: "#EF4444" },
  { name: "Wine", primary: "#7F1D1D", accent: "#F59E0B", success: "#059669", danger: "#DC2626" },
];

export default function Settings() {
  const { t } = useLang();
  const { user } = useAuth();
  const [logo, setLogo] = useState(null);
  const [companyName, setCompanyName] = useState("Minera OOMINING");
  const [savingName, setSavingName] = useState(false);
  const [brand, setBrand] = useState({ sidebar_title: "", sidebar_byline: "", sidebar_tagline: "" });
  const [savingBrand, setSavingBrand] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setLogo(r.data.logo);
      if (r.data.name) setCompanyName(r.data.name);
      setBrand({
        sidebar_title: r.data.sidebar_title || "",
        sidebar_byline: r.data.sidebar_byline || "",
        sidebar_tagline: r.data.sidebar_tagline || "",
      });
    }).catch(() => {});
    api.get("/settings/theme").then((r) => setTheme({ ...DEFAULT_THEME, ...r.data })).catch(() => {});
  }, []);

  const saveName = async () => {
    setSavingName(true);
    try {
      const { data } = await api.put("/settings/company", { name: companyName });
      setCompanyName(data.name);
      toast.success("✓");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingName(false); }
  };

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.put("/settings/company", {
        sidebar_title: brand.sidebar_title,
        sidebar_byline: brand.sidebar_byline,
        sidebar_tagline: brand.sidebar_tagline,
      });
      setBrand({
        sidebar_title: data.sidebar_title || "",
        sidebar_byline: data.sidebar_byline || "",
        sidebar_tagline: data.sidebar_tagline || "",
      });
      window.dispatchEvent(new Event("company-logo-updated"));   // reusa el bus para refrescar Sidebar
      toast.success("Marca del sidebar actualizada");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingBrand(false); }
  };

  const notify = () => window.dispatchEvent(new Event("company-logo-updated"));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/company/logo", fd);
      setLogo(data.logo);
      notify();
      toast.success("✓");
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async () => {
    try {
      await api.delete("/settings/company/logo");
      setLogo(null);
      notify();
      toast.success("✓");
    } catch (err) { toast.error(apiError(err)); }
  };

  const previewTheme = (patch) => {
    const { name: _name, ...colors } = patch;
    const next = { ...theme, ...colors };
    setTheme(next);
    applyTheme(next);
  };

  const saveTheme = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/theme", theme);
      applyTheme(data);
      window.dispatchEvent(new CustomEvent("theme-updated", { detail: data }));
      toast.success(t("themeSaved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const resetTheme = () => {
    setTheme(DEFAULT_THEME);
    applyTheme(DEFAULT_THEME);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline-label">{t("settings")}</p>
        <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1">{t("settings")}</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-xl space-y-6">
        <div>
          <Label className="overline-label" htmlFor="company-name-input">Nombre de la empresa</Label>
          <p className="text-sm text-slate-500 mt-1 mb-3">Aparece como encabezado en todos los PDFs, correos y reportes generados.</p>
          <div className="flex gap-2">
            <Input
              id="company-name-input"
              data-testid="company-name-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Minera OOMINING"
              disabled={user?.role !== "admin"}
              className="max-w-xs"
            />
            {user?.role === "admin" && (
              <Button
                data-testid="company-name-save"
                onClick={saveName}
                disabled={savingName || !companyName.trim()}
                className="bg-blue-900 hover:bg-blue-800"
              >
                {savingName ? t("saving") : t("save")}
              </Button>
            )}
          </div>
        </div>
        <div className="pt-6 border-t border-slate-100 space-y-4" data-testid="branding-section">
          <div>
            <Label className="overline-label">Marca del sidebar</Label>
            <p className="text-sm text-slate-500 mt-1 mb-3">
              Los 3 textos visibles en el panel lateral de navegación (título grande, firma pequeña, y descripción). El logo se conserva independiente.
            </p>
          </div>
          <div className="grid gap-3 max-w-lg">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="brand-title">Título (grande)</Label>
              <Input
                id="brand-title"
                data-testid="brand-title-input"
                value={brand.sidebar_title}
                onChange={(e) => setBrand({ ...brand, sidebar_title: e.target.value })}
                placeholder="UG Managment"
                disabled={user?.role !== "admin"}
                maxLength={60}
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Si contiene 2+ palabras, la primera se muestra en negro y el resto en azul (estilo institucional).
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="brand-byline">Firma (línea pequeña)</Label>
              <Input
                id="brand-byline"
                data-testid="brand-byline-input"
                value={brand.sidebar_byline}
                onChange={(e) => setBrand({ ...brand, sidebar_byline: e.target.value })}
                placeholder="by OOMINING"
                disabled={user?.role !== "admin"}
                maxLength={60}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="brand-tagline">Descripción (bajo la firma)</Label>
              <Input
                id="brand-tagline"
                data-testid="brand-tagline-input"
                value={brand.sidebar_tagline}
                onChange={(e) => setBrand({ ...brand, sidebar_tagline: e.target.value })}
                placeholder="UG Mine Management"
                disabled={user?.role !== "admin"}
                maxLength={60}
              />
            </div>
            {user?.role === "admin" && (
              <Button
                data-testid="brand-save"
                onClick={saveBrand}
                disabled={savingBrand}
                className="bg-blue-900 hover:bg-blue-800 w-fit"
              >
                {savingBrand ? t("saving") : "Guardar marca del sidebar"}
              </Button>
            )}
          </div>
        </div>
        <div className="pt-6 border-t border-slate-100">
        <Label className="overline-label">{t("companyLogo")}</Label>
        <p className="text-sm text-slate-500 mt-1 mb-6">{t("logoHint")}</p>
        <div className="flex items-center gap-6">
          <div className="h-24 w-40 border border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
            {logo ? (
              <img src={logo} alt="logo" data-testid="company-logo-preview" className="max-h-20 max-w-36 object-contain" />
            ) : (
              <div className="text-slate-400 flex flex-col items-center gap-1 text-xs" data-testid="no-logo-placeholder">
                <Buildings size={22} /> {t("noLogo")}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Button data-testid="upload-logo-button" onClick={() => fileRef.current?.click()} className="bg-blue-900 hover:bg-blue-800 gap-2 w-full">
              <UploadSimple size={16} weight="bold" /> {t("uploadLogo")}
            </Button>
            {logo && (
              <Button variant="outline" data-testid="remove-logo-button" onClick={remove} className="gap-2 w-full text-red-600 hover:text-red-700">
                <Trash size={16} /> {t("removeLogo")}
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-file-input" onChange={upload} />
          </div>
        </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-3xl" data-testid="theme-section">
        <div className="flex items-center gap-2 mb-1">
          <Palette size={20} weight="duotone" className="text-blue-900" />
          <Label className="overline-label">{t("colorScheme")}</Label>
        </div>
        <p className="text-sm text-slate-500 mb-6">{t("colorSchemeHint")}</p>

        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">{t("presets")}</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                data-testid={`theme-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => previewTheme(p)}
                className="flex items-center gap-2 border border-slate-200 rounded-full pl-1.5 pr-3 py-1 hover:border-slate-400 transition-colors"
              >
                <span className="flex -space-x-1">
                  <span className="h-4 w-4 rounded-full border border-white" style={{ backgroundColor: p.primary }} />
                  <span className="h-4 w-4 rounded-full border border-white" style={{ backgroundColor: p.accent }} />
                </span>
                <span className="text-xs font-medium">{p.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: "primary", label: t("colorPrimary") },
            { key: "accent", label: t("colorAccent") },
            { key: "success", label: t("colorSuccess") },
            { key: "danger", label: t("colorDanger") },
          ].map((c) => (
            <div key={c.key} className="space-y-1.5">
              <Label className="overline-label">{c.label}</Label>
              <div className="flex items-center gap-2">
                <input
                  data-testid={`color-picker-${c.key}`}
                  type="color"
                  value={theme[c.key]}
                  onChange={(e) => previewTheme({ [c.key]: e.target.value })}
                  className="h-10 w-14 border border-slate-200 rounded-md cursor-pointer"
                />
                <Input
                  data-testid={`color-hex-${c.key}`}
                  value={theme[c.key]}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTheme((s) => ({ ...s, [c.key]: v }));
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) applyTheme({ ...theme, [c.key]: v });
                  }}
                  className="font-mono text-xs h-10"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-6">
          <Button data-testid="theme-save-button" onClick={saveTheme} disabled={saving} className="bg-blue-900 hover:bg-blue-800 gap-2">
            {saving ? t("saving") : t("save")}
          </Button>
          <Button variant="outline" data-testid="theme-reset-button" onClick={resetTheme} className="gap-2">
            <ArrowCounterClockwise size={14} weight="bold" /> {t("resetToDefault")}
          </Button>
        </div>
      </div>

      {user?.role === "admin" && <CareersHeroPanel />}

      {(user?.role === "admin" || user?.role === "manager" || user?.role === "jefatura") && <WeeklyReportPanel />}

      {user?.role === "admin" && <ReliabilityAlertsPanel />}

      {user?.role === "admin" && <ResendDiagnosticsPanel />}

      {user?.role === "admin" && (
        <div className="mt-6">
          <SelfieRulesPanel />
        </div>
      )}
      {user?.role === "admin" && <SecurityPanel />}

      {(user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor") && (
        <HazardsPanel canManage={["admin", "manager"].includes(user?.role)} />
      )}

      {(user?.role === "admin" || user?.role === "manager") && (
        <FrontPlanDeviationPanel canManage={["admin", "manager"].includes(user?.role)} />
      )}

      {(user?.role === "admin" || user?.role === "manager") && (
        <PositionsCatalogPanel />
      )}

      {(user?.role === "admin" || user?.role === "manager") && <WorksiteGeofencePanel />}

      {user?.role === "admin" && <BackupRestorePanel />}

      {(user?.role === "admin" || user?.role === "manager") && <DevSectionsPanel />}

      {(user?.role === "admin" || user?.role === "manager") && <ProjectsPanel />}

      {(user?.role === "admin" || user?.role === "manager" || user?.role === "jefatura") && <CargosAreasPanel />}

      <CatalogSettings />
    </div>
  );
}

/**
 * Careers Hero Panel — Configura el hero de la página pública /careers
 * ("Trabaja con Nosotros"). Permite personalizar título, subtítulo, color
 * de fondo y opcionalmente una imagen de fondo.
 */
function CareersHeroPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setCfg({
        careers_hero_title: r.data.careers_hero_title || "",
        careers_hero_subtitle: r.data.careers_hero_subtitle || "",
        careers_hero_title_en: r.data.careers_hero_title_en || "",
        careers_hero_subtitle_en: r.data.careers_hero_subtitle_en || "",
        careers_hero_bg_color: r.data.careers_hero_bg_color || "#0c183c",
        careers_hero_bg_image: r.data.careers_hero_bg_image || "",
      });
    }).catch(() => {
      setCfg({
        careers_hero_title: "",
        careers_hero_subtitle: "",
        careers_hero_title_en: "",
        careers_hero_subtitle_en: "",
        careers_hero_bg_color: "#0c183c",
        careers_hero_bg_image: "",
      });
    });
  }, []);

  const [tab, setTab] = useState("es");

  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", {
        careers_hero_title: cfg.careers_hero_title,
        careers_hero_subtitle: cfg.careers_hero_subtitle,
        careers_hero_title_en: cfg.careers_hero_title_en,
        careers_hero_subtitle_en: cfg.careers_hero_subtitle_en,
        careers_hero_bg_color: cfg.careers_hero_bg_color,
      });
      setCfg((c) => ({
        ...c,
        careers_hero_title: data.careers_hero_title,
        careers_hero_subtitle: data.careers_hero_subtitle,
        careers_hero_title_en: data.careers_hero_title_en,
        careers_hero_subtitle_en: data.careers_hero_subtitle_en,
        careers_hero_bg_color: data.careers_hero_bg_color,
      }));
      toast.success("Portada de empleos actualizada");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/company/careers-hero", fd);
      setCfg((c) => ({ ...c, careers_hero_bg_image: data.careers_hero_bg_image }));
      toast.success("Fondo actualizado");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const removeImage = async () => {
    try {
      await api.delete("/settings/company/careers-hero");
      setCfg((c) => ({ ...c, careers_hero_bg_image: "" }));
      toast.success("Fondo removido");
    } catch (err) { toast.error(apiError(err)); }
  };

  const hasImage = !!cfg.careers_hero_bg_image;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-3xl" data-testid="careers-hero-panel">
      <div className="flex items-center gap-2 mb-1">
        <Briefcase size={20} weight="duotone" className="text-blue-900" />
        <Label className="overline-label !m-0">Página de Empleos · Portada</Label>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Personaliza el encabezado de <code>/careers</code> (&quot;Trabaja con Nosotros&quot;): texto, color de fondo y/o imagen.
        Cuando cargas una imagen, ésta reemplaza al color (se aplica un velo oscuro para legibilidad).
      </p>

      {/* Preview */}
      <div
        className="rounded-lg overflow-hidden mb-6 border border-slate-200"
        style={{
          backgroundColor: cfg.careers_hero_bg_color || "#0c183c",
          backgroundImage: hasImage ? `url(${cfg.careers_hero_bg_image})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="careers-hero-preview"
      >
        <div className={`px-6 py-8 relative ${hasImage ? "bg-black/50" : ""}`}>
          <p className="text-[10px] uppercase tracking-widest text-blue-200 mb-1">Vista previa</p>
          <h2 className="text-white font-heading font-black text-2xl sm:text-3xl tracking-tight">
            {cfg.careers_hero_title || "Trabaja con Nosotros"}
          </h2>
          <p className="text-blue-100 mt-2 text-xs sm:text-sm max-w-xl whitespace-pre-line">
            {cfg.careers_hero_subtitle || "Únete a nuestro equipo de minería subterránea."}
          </p>
        </div>
      </div>

      <div className="grid gap-4 max-w-2xl">
        {/* Tabs ES / EN */}
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs w-fit">
          <button
            data-testid="careers-lang-es"
            type="button"
            onClick={() => setTab("es")}
            className={`px-3 py-1.5 font-bold ${tab === "es" ? "bg-blue-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
          >
            🇲🇽 Español (default)
          </button>
          <button
            data-testid="careers-lang-en"
            type="button"
            onClick={() => setTab("en")}
            className={`px-3 py-1.5 font-bold border-l border-slate-200 ${tab === "en" ? "bg-blue-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
          >
            🇬🇧 English
          </button>
        </div>

        {tab === "es" ? (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="careers-title">
                Título del hero
              </Label>
              <Input
                id="careers-title"
                data-testid="careers-title-input"
                value={cfg.careers_hero_title}
                onChange={(e) => setCfg({ ...cfg, careers_hero_title: e.target.value })}
                placeholder="Trabaja con Nosotros"
                maxLength={120}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="careers-subtitle">
                Subtítulo / descripción
              </Label>
              <Textarea
                id="careers-subtitle"
                data-testid="careers-subtitle-input"
                rows={3}
                value={cfg.careers_hero_subtitle}
                onChange={(e) => setCfg({ ...cfg, careers_hero_subtitle: e.target.value })}
                placeholder="Únete a nuestro equipo de minería subterránea..."
                maxLength={400}
              />
              <p className="text-[10px] text-slate-400 mt-1">Máx 400 caracteres · admite saltos de línea.</p>
            </div>
          </>
        ) : (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="careers-title-en">
                Hero title (English)
              </Label>
              <Input
                id="careers-title-en"
                data-testid="careers-title-input-en"
                value={cfg.careers_hero_title_en}
                onChange={(e) => setCfg({ ...cfg, careers_hero_title_en: e.target.value })}
                placeholder="Work With Us"
                maxLength={120}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500" htmlFor="careers-subtitle-en">
                Subtitle / description (English)
              </Label>
              <Textarea
                id="careers-subtitle-en"
                data-testid="careers-subtitle-input-en"
                rows={3}
                value={cfg.careers_hero_subtitle_en}
                onChange={(e) => setCfg({ ...cfg, careers_hero_subtitle_en: e.target.value })}
                placeholder="Join our underground mining team..."
                maxLength={400}
              />
              <p className="text-[10px] text-slate-400 mt-1">Max 400 characters · line breaks allowed.</p>
            </div>
          </>
        )}

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Color de fondo</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="color"
              data-testid="careers-bg-color"
              value={cfg.careers_hero_bg_color}
              onChange={(e) => setCfg({ ...cfg, careers_hero_bg_color: e.target.value })}
              className="h-10 w-14 border border-slate-200 rounded-md cursor-pointer"
              disabled={hasImage}
            />
            <Input
              data-testid="careers-bg-hex"
              value={cfg.careers_hero_bg_color}
              onChange={(e) => setCfg({ ...cfg, careers_hero_bg_color: e.target.value })}
              className="font-mono text-xs h-10 max-w-[140px]"
              disabled={hasImage}
            />
            {hasImage && (
              <span className="text-[11px] text-slate-500 italic">
                Color oculto mientras haya imagen de fondo.
              </span>
            )}
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Imagen de fondo (opcional)</Label>
          <p className="text-[11px] text-slate-500 mt-1 mb-2">
            PNG, JPG o WEBP · máx 3MB · mín 200×100. Si subes una imagen, tendrá prioridad sobre el color.
          </p>
          <div className="flex items-center gap-3">
            <Button
              data-testid="careers-bg-upload"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-blue-900 hover:bg-blue-800 gap-2"
            >
              <UploadSimple size={14} weight="bold" /> {uploading ? "Subiendo..." : (hasImage ? "Cambiar imagen" : "Subir imagen")}
            </Button>
            {hasImage && (
              <Button
                variant="outline"
                data-testid="careers-bg-remove"
                onClick={removeImage}
                className="gap-2 text-red-600 hover:text-red-700"
              >
                <Trash size={14} /> Quitar imagen
              </Button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="careers-bg-file-input"
              onChange={uploadImage}
            />
          </div>
        </div>

        <div className="pt-2">
          <Button
            data-testid="careers-hero-save"
            onClick={save}
            disabled={saving}
            className="bg-blue-900 hover:bg-blue-800"
          >
            {saving ? "Guardando..." : "Guardar textos y color"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResendDiagnosticsPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/settings/email-test", {});
      setResult(data);
      if (data.ok) toast.success("Correo de prueba enviado");
      else toast.error(data.hint || `Status: ${data.status}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };
  const ok = result?.ok;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-3xl">
      <Label className="overline-label">Diagnóstico de correo (Resend)</Label>
      <p className="text-sm text-slate-500 mt-1 mb-4">
        Envía un correo de prueba a tu cuenta para verificar que <code>ugooperations.com</code> esté verificado en Resend y que <code>SENDER_EMAIL</code> apunte a una dirección de ese dominio.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <Button data-testid="resend-test-btn" onClick={run} disabled={busy} className="bg-blue-900 hover:bg-blue-800">
          {busy ? "Enviando..." : "Enviar correo de prueba"}
        </Button>
        {result && (
          <div className={`px-3 py-2 rounded text-xs ${ok ? "bg-emerald-50 border border-emerald-300 text-emerald-900" : "bg-red-50 border border-red-300 text-red-900"}`}
                data-testid="resend-test-result">
            <p><b>{ok ? "✓ Enviado" : "✗ Falló"}</b> · Destino: <code>{result.to}</code> · Sender: <code>{result.sender || "sin configurar"}</code></p>
            <p className="mt-1 opacity-80">Status: {result.status}</p>
            {result.hint && <p className="mt-1 font-semibold">Sugerencia: {result.hint}</p>}
          </div>
        )}
      </div>
      <details className="mt-4 text-xs text-slate-500">
        <summary className="cursor-pointer font-semibold">¿Cómo verificar el dominio en Resend?</summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1">
          <li>Entra a <a className="text-blue-900 underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a> con la cuenta que emite los correos.</li>
          <li>Añade el dominio <code>ugooperations.com</code> y copia los registros DNS que Resend te muestra (MX, TXT SPF, TXT DKIM, TXT DMARC).</li>
          <li>Pega esos registros en el panel DNS de tu proveedor del dominio.</li>
          <li>Espera 15-60 min y presiona <b>Verify</b> en Resend hasta que aparezca ✓ verificado.</li>
          <li>Actualiza <code>SENDER_EMAIL</code> en <code>backend/.env</code> a algo como <code>no-reply@ugooperations.com</code> y reinicia el backend.</li>
          <li>Regresa aquí y presiona &quot;Enviar correo de prueba&quot;.</li>
        </ol>
      </details>
    </div>
  );
}

/**
 * Reliability Alerts — Configura destinatarios (email + SMS) que reciben
 * notificacion automatica cuando la auto-gen de OT crea una urgente en
 * equipo criticidad A. El planner reacciona dentro del turno, no al dia
 * siguiente.
 */
function ReliabilityAlertsPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");

  useEffect(() => {
    api.get("/settings/maintenance-alerts").then((r) => setCfg(r.data)).catch(() => {
      setCfg({ emails: [], phones: [], criticalities: ["A"], kinds: ["correctivo"], enabled: true });
    });
  }, []);

  if (!cfg) return null;

  const addEmail = () => {
    const v = newEmail.trim();
    if (!v || !v.includes("@")) { toast.error("Correo inválido"); return; }
    if (cfg.emails.includes(v)) { toast.error("Ya está en la lista"); return; }
    setCfg({ ...cfg, emails: [...cfg.emails, v] });
    setNewEmail("");
  };
  const addPhone = () => {
    const v = newPhone.trim();
    if (!v) return;
    if (cfg.phones.includes(v)) { toast.error("Ya está en la lista"); return; }
    setCfg({ ...cfg, phones: [...cfg.phones, v] });
    setNewPhone("");
  };
  const removeEmail = (e) => setCfg({ ...cfg, emails: cfg.emails.filter((x) => x !== e) });
  const removePhone = (p) => setCfg({ ...cfg, phones: cfg.phones.filter((x) => x !== p) });

  const toggleCrit = (c) => {
    const arr = cfg.criticalities.includes(c)
      ? cfg.criticalities.filter((x) => x !== c) : [...cfg.criticalities, c];
    setCfg({ ...cfg, criticalities: arr });
  };
  const toggleKind = (k) => {
    const arr = cfg.kinds.includes(k) ? cfg.kinds.filter((x) => x !== k) : [...cfg.kinds, k];
    setCfg({ ...cfg, kinds: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/maintenance-alerts", cfg);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/maintenance-alerts/test", {});
      if (r.data.sent) toast.success(`Alerta de prueba enviada · matched=${r.data.matched}`);
      else toast.info(r.data.reason || "Sin acción");
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-3xl" data-testid="reliability-alerts-panel">
      <div className="flex items-center gap-2 mb-1">
        <BellRinging size={18} weight="duotone" className="text-orange-600" />
        <Label className="overline-label !m-0">Alertas de confiabilidad (Mantenimiento)</Label>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Cuando el auto-generador de OT crea una orden urgente en equipo de la criticidad configurada,
        el sistema envía SMS + email a los planners para que reaccionen dentro del turno.
      </p>

      <label className="flex items-center gap-2 mb-4 cursor-pointer">
        <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
               data-testid="alerts-enabled" className="w-4 h-4 accent-blue-900" />
        <span className="text-sm font-semibold">Habilitar alertas automáticas</span>
      </label>

      <div className="grid md:grid-cols-2 gap-6 mb-5">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Criticidades que disparan alerta</Label>
          <div className="flex gap-2 mt-2">
            {["A", "B", "C"].map((c) => (
              <button
                key={c}
                onClick={() => toggleCrit(c)}
                data-testid={`alerts-crit-${c}`}
                className={`px-4 py-1.5 rounded-md text-sm font-bold border transition ${
                  cfg.criticalities.includes(c) ? "bg-red-600 text-white border-red-700" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
                }`}
              >{c}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Tipos de OT</Label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {["correctivo", "PM250", "PM500", "PM1000", "PM2000"].map((k) => (
              <button
                key={k}
                onClick={() => toggleKind(k)}
                data-testid={`alerts-kind-${k}`}
                className={`px-3 py-1.5 rounded-md text-xs font-bold border transition ${
                  cfg.kinds.includes(k) ? "bg-blue-900 text-white border-blue-950" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"
                }`}
              >{k}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Emails de planners</Label>
          <div className="flex gap-2 mt-2">
            <Input placeholder="planner@empresa.com" value={newEmail}
                   onChange={(e) => setNewEmail(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && addEmail()}
                   data-testid="alerts-email-input" />
            <Button variant="outline" onClick={addEmail} data-testid="alerts-email-add"><Plus size={14} /></Button>
          </div>
          <ul className="mt-3 space-y-1.5">
            {cfg.emails.length === 0 && <li className="text-xs text-slate-400 italic">Sin destinatarios</li>}
            {cfg.emails.map((e) => (
              <li key={e} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono"
                  data-testid={`alerts-email-${e}`}>
                <span>{e}</span>
                <button onClick={() => removeEmail(e)} className="text-red-500 hover:text-red-700"><X size={12} /></button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Teléfonos (SMS · +52)</Label>
          <div className="flex gap-2 mt-2">
            <Input placeholder="5551234567" value={newPhone}
                   onChange={(e) => setNewPhone(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && addPhone()}
                   data-testid="alerts-phone-input" />
            <Button variant="outline" onClick={addPhone} data-testid="alerts-phone-add"><Plus size={14} /></Button>
          </div>
          <ul className="mt-3 space-y-1.5">
            {cfg.phones.length === 0 && <li className="text-xs text-slate-400 italic">Sin destinatarios</li>}
            {cfg.phones.map((p) => (
              <li key={p} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono"
                  data-testid={`alerts-phone-${p}`}>
                <span>{p}</span>
                <button onClick={() => removePhone(p)} className="text-red-500 hover:text-red-700"><X size={12} /></button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex gap-2 mt-6">
        <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="alerts-save">
          {saving ? "Guardando..." : "Guardar configuración"}
        </Button>
        <Button variant="outline" onClick={test} disabled={testing}
                data-testid="alerts-test"
                className="border-orange-500 text-orange-700 hover:bg-orange-50">
          <BellRinging size={14} weight="bold" className="mr-1.5" />
          {testing ? "Enviando..." : "Enviar alerta de prueba"}
        </Button>
      </div>
    </div>
  );
}

